/**
 * useServerListParams — stato comune per le liste paginate lato server
 * (DataGrid MUI in `paginationMode="server"` + `sortingMode="server"`).
 *
 * Centralizza il pattern usato da Person/Business/Task/(Message):
 *   - state paginationModel (page 0-based MUI) + sortModel
 *   - conversione a `backendParams` pronti da mandare al REST Liferay
 *     (page 1-based, sort in sintassi "field:asc,field:desc")
 *   - helper `resetPage()` da chiamare quando cambiano filtri esterni
 *     (es. search), per evitare di restare su una pagina oltre totalCount.
 *
 * Non gestisce direttamente il filtro `search`: quello rimane nel
 * componente (tipicamente via `useServerSearch`) e viene mergiato con
 * backendParams al momento del dispatch.
 *
 * Esempio d'uso (PersonList):
 * ```tsx
 * const { query } = useServerSearch();
 * const { paginationModel, setPaginationModel, sortModel, setSortModel,
 *         resetPage, backendParams } = useServerListParams();
 *
 * useEffect(() => { resetPage(); }, [query, sortModel, resetPage]);
 * useEffect(() => {
 *   dispatch(fetchPersons({ ...backendParams, search: query || undefined }));
 * }, [backendParams, query, dispatch]);
 *
 * <DataGrid paginationMode="server" sortingMode="server"
 *   paginationModel={paginationModel} onPaginationModelChange={setPaginationModel}
 *   sortModel={sortModel} onSortModelChange={setSortModel}
 *   rowCount={totalCount} ... />
 * ```
 */

import { useCallback, useMemo, useState } from 'react';

// Digitiamo i sort/pagination model in modo strutturale per evitare una
// dipendenza diretta da @mui/x-data-grid dentro @crm/shared (che vuole
// restare UI-library agnostic).
export interface ListPaginationModel {
  page: number;      // 0-based (come MUI DataGrid)
  pageSize: number;
}
export interface ListSortItem {
  field: string;
  // Allineato a MUI GridSortItem (required + GridSortDirection include
  // null e undefined come "sort rimosso" / "nessun ordinamento").
  sort: 'asc' | 'desc' | null | undefined;
}
// readonly per combaciare con `GridSortModel` di MUI (che e' `readonly GridSortItem[]`)
// e permettere l'assegnazione diretta del setter a `onSortModelChange`.
export type ListSortModel = ReadonlyArray<ListSortItem>;

export interface ServerListBackendParams {
  page: number;      // 1-based (come Liferay REST)
  pageSize: number;
  sort?: string;     // "field:asc,field2:desc" oppure undefined
}

interface UseServerListParamsOptions {
  initialPageSize?: number;
  initialSort?: ListSortModel;
}

interface UseServerListParamsReturn {
  paginationModel: ListPaginationModel;
  setPaginationModel: (m: ListPaginationModel) => void;
  sortModel: ListSortModel;
  setSortModel: (m: ListSortModel) => void;
  /** Forza la page a 0: da chiamare quando cambiano filtri esterni. */
  resetPage: () => void;
  /** Params gia' convertiti per il REST Liferay (page 1-based, sort string). */
  backendParams: ServerListBackendParams;
}

/** Converte un ListSortModel nella sintassi backend `field:asc,field:desc`. */
export function toSortParam(model: ListSortModel): string | undefined {
  if (model.length === 0) return undefined;
  return model.map((s) => `${s.field}:${s.sort ?? 'asc'}`).join(',');
}

export function useServerListParams(
  options: UseServerListParamsOptions = {}
): UseServerListParamsReturn {
  const { initialPageSize = 10, initialSort = [] } = options;

  const [paginationModel, setPaginationModel] = useState<ListPaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  });
  const [sortModel, setSortModel] = useState<ListSortModel>(initialSort);

  const resetPage = useCallback(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, []);

  const backendParams = useMemo<ServerListBackendParams>(
    () => ({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      sort: toSortParam(sortModel),
    }),
    [paginationModel, sortModel]
  );

  return {
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    resetPage,
    backendParams,
  };
}
